import React from "react";
import { createApi } from "../../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { AlertState, Dispatcher } from "./siteAboutInterfaces";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { WebhooksTab } from "../WebhooksTab";
import WebhooksTabEditMode from "../WebhooksTabEditMode";
import { extractWebhookFieldErrors } from "../../../../utils/webhookAuth";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";
import {
  WebhookFieldErrorsById,
  WebhooksPatchMap,
  mergeWebhookById,
  pendingWebhookPatches,
} from "./siteWebhooks";

type PatchOutcome =
  | { id: number; ok: true; data: any }
  | { id: number; ok: false; reason: AxiosError };

/**
 * Save Changes for the Webhooks tab (ENG-2233): one PATCH per webhook that
 * has pending edits. Rows that save are merged back immediately; a row the
 * server rejects with a field-keyed 400 stays in edit mode with its messages
 * inline, and only its edits remain pending.
 */
export const handlePatchWebhooksData = (
  webhooksInfo: any,
  setWebhooksInfo: any,
  webhooksPatchObject: WebhooksPatchMap,
  setWebhooksPatchObject: any,
  setWebhooksChangesMade: any,
  setActiveTab: any,
  setAlertState: any,
  setBackdropLoadingScreenOn: Dispatcher<boolean>,
  setEditMode: Dispatcher<boolean>,
  navigate: NavigateFunction
) => {
  const AxiosUI = createApi(``);

  const retryFunction = () => {
    handlePatchWebhooksData(
      webhooksInfo,
      setWebhooksInfo,
      webhooksPatchObject,
      setWebhooksPatchObject,
      setWebhooksChangesMade,
      setActiveTab,
      setAlertState,
      setBackdropLoadingScreenOn,
      setEditMode,
      navigate
    );
  };

  const { setScheduleGridEditable, setBackupWebhookData } =
    useSiteAboutStore.getState();

  const finishSaved = (webhooks: any[]) => {
    setWebhooksPatchObject({});
    setWebhooksChangesMade(false);
    setScheduleGridEditable(false);
    setEditMode(false);
    setAlertState((previousState: AlertState) => {
      return { ...previousState, changeSuccess: true };
    });
    setActiveTab(<WebhooksTab webhooksInfo={webhooks} />);
    setBackdropLoadingScreenOn(false);
  };

  const pending = pendingWebhookPatches(webhooksPatchObject);
  if (pending.length === 0) {
    finishSaved(webhooksInfo);
    return;
  }

  Promise.all(
    pending.map(
      ([id, patch]): Promise<PatchOutcome> =>
        AxiosUI.patch(`/webhook_customer/${id}/`, patch).then(
          (response: AxiosResponse) => ({ id, ok: true, data: response.data }),
          (reason: AxiosError) => ({ id, ok: false, reason })
        )
    )
  ).then((outcomes) => {
    let updated: any[] = Array.isArray(webhooksInfo) ? webhooksInfo : [];
    const fieldErrorsById: WebhookFieldErrorsById = {};
    const otherFailures: AxiosError[] = [];

    outcomes.forEach((outcome) => {
      if (outcome.ok) {
        updated = mergeWebhookById(updated, outcome.data);
        return;
      }
      // A 400 names the rejected fields (auth_endpoint SSRF refusal, an
      // auth_body that can't authenticate, ...).
      const fieldErrors = extractWebhookFieldErrors(outcome.reason);
      if (Object.keys(fieldErrors).length > 0) {
        fieldErrorsById[outcome.id] = fieldErrors;
      } else {
        otherFailures.push(outcome.reason);
      }
    });

    // Whatever saved is saved, whatever the other rows did.
    setWebhooksInfo(updated);
    setBackupWebhookData(updated);

    if (otherFailures.length > 0) {
      universalAPIErrorHandler(
        otherFailures[0],
        navigate,
        retryFunction,
        () => {
          setWebhooksPatchObject({});
          setWebhooksChangesMade(false);
        }
      );
      setEditMode(false);
      setAlertState((previousState: AlertState) => {
        return { ...previousState, webhookChangesFail: true };
      });
      setActiveTab(<WebhooksTab webhooksInfo={updated} />);
      setBackdropLoadingScreenOn(false);
      return;
    }

    if (Object.keys(fieldErrorsById).length > 0) {
      // Stay in edit mode with the rejected rows' pending changes intact and
      // show the messages on those fields.
      const remaining: WebhooksPatchMap = {};
      pending.forEach(([id, patch]) => {
        if (fieldErrorsById[id]) remaining[id] = patch;
      });
      setWebhooksPatchObject(remaining);
      setActiveTab(
        <WebhooksTabEditMode
          webhooksInfo={updated}
          setWebhooksPatchObject={setWebhooksPatchObject}
          setWebhooksChangesMade={setWebhooksChangesMade}
          fieldErrorsById={fieldErrorsById}
        />
      );
      setBackdropLoadingScreenOn(false);
      return;
    }

    finishSaved(updated);
  });
};
